import { formatCents } from "@caloteiros/shared";
import { useState } from "react";
import type { ReactElement } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell";
import { ExpensesTab } from "../components/group/ExpensesTab";
import { PeopleTab } from "../components/group/PeopleTab";
import { SummaryTab } from "../components/group/SummaryTab";
import { IconDownload, IconLink, IconPlus, IconShare, IconTrash, IconWhatsApp } from "../components/Icons";
import { ConfirmDialog, EmptyState, LoadingScreen, Sheet } from "../components/ui";
import { firstName, formatShortDate } from "../lib/format";
import { useBalances, useDeleteGroup, useExpenses, useGroup, useSetShare } from "../lib/queries";
import { buildSummaryText, copyToClipboard, downloadCsv, openWhatsApp } from "../lib/share";
import { useToast } from "../lib/toast";

type Tab = "resumo" | "despesas" | "pessoas";

const TABS: { id: Tab; label: string }[] = [
  { id: "resumo", label: "Resumo" },
  { id: "despesas", label: "Despesas" },
  { id: "pessoas", label: "Pessoas" },
];

export function GroupPage(): ReactElement {
  const { groupId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [tab, setTab] = useState<Tab>("resumo");
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const group = useGroup(groupId);
  const balances = useBalances(groupId, "pairwise");
  const expenses = useExpenses(groupId);
  const setShare = useSetShare(groupId);
  const deleteGroup = useDeleteGroup();

  if (group.isLoading) return <LoadingScreen />;

  if (group.isError || !group.data) {
    return (
      <>
        <PageHeader title="Rolê" back="/app" />
        <main className="app-content">
          <EmptyState
            emoji="🤔"
            title="Não achei esse rolê"
            description="Ele pode ter sido apagado, ou você não participa mais dele."
            action={
              <Link className="btn btn-primary" to="/app">
                Voltar ao início
              </Link>
            }
          />
        </main>
      </>
    );
  }

  const data = group.data;
  const nameOf = (personId: string): string =>
    balances.data?.people.find((person) => person.id === personId)?.displayName ?? "Alguém";

  const shareOnWhatsApp = (): void => {
    const transfers = (balances.data?.transfers ?? []).map((transfer) => ({
      fromName: firstName(nameOf(transfer.fromPersonId)),
      toName: firstName(nameOf(transfer.toPersonId)),
      amountCents: transfer.amountCents,
      pixKey: transfer.toPixKey,
    }));
    openWhatsApp(buildSummaryText(data.name, transfers));
  };

  const exportCsv = (): void => {
    const rows: (string | number)[][] = [["Data", "Descrição", "Quem pagou", "Valor", "Divisão"]];
    for (const expense of expenses.data ?? []) {
      rows.push([
        formatShortDate(expense.occurredAt),
        expense.description,
        expense.payers.map((payer) => nameOf(payer.personId)).join(" + "),
        formatCents(expense.amountCents),
        expense.shares
          .map((share) => `${nameOf(share.personId)}: ${formatCents(share.amountCents)}`)
          .join(" | "),
      ]);
    }
    downloadCsv(`caloteiros-${data.name.toLowerCase().replace(/\s+/g, "-")}.csv`, rows);
  };

  return (
    <>
      <PageHeader
        title={`${data.emoji ?? "🧾"} ${data.name}`}
        back="/app"
        actions={
          <button
            type="button"
            className="btn-icon"
            aria-label="Compartilhar e opções"
            onClick={() => setShareOpen(true)}
          >
            <IconShare />
          </button>
        }
      />

      <main className="app-content stack-lg">
        <nav className="tabs" aria-label="Seções do rolê">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={tab === item.id ? "tab-active" : ""}
              aria-current={tab === item.id ? "page" : undefined}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {tab === "resumo" ? <SummaryTab group={data} /> : null}
        {tab === "despesas" ? <ExpensesTab group={data} /> : null}
        {tab === "pessoas" ? <PeopleTab group={data} /> : null}
      </main>

      <Link
        className="fab"
        to={`/app/grupos/${groupId}/despesas/nova`}
        aria-label="Lançar despesa"
      >
        <IconPlus />
        Despesa
      </Link>

      <Sheet open={shareOpen} onClose={() => setShareOpen(false)} title="Compartilhar rolê">
        <div className="stack-lg">
          <button type="button" className="btn btn-primary btn-block" onClick={shareOnWhatsApp}>
            <IconWhatsApp />
            Mandar resumo no WhatsApp
          </button>

          <div className="stack">
            <span className="label">Link público</span>
            <p className="hint">
              Quem tem o link vê os valores e a chave Pix, sem precisar de conta — e não
              consegue alterar nada.
            </p>

            {data.shareEnabled && data.shareUrl ? (
              <div className="stack">
                <div className="pix-box">
                  <IconLink width={16} height={16} />
                  <span className="pix-key">{data.shareUrl}</span>
                </div>
                <div className="row">
                  <button
                    type="button"
                    className="btn btn-subtle grow"
                    onClick={() => {
                      void copyToClipboard(data.shareUrl as string).then((ok) =>
                        ok ? toast.success("Link copiado.") : toast.error("Não consegui copiar."),
                      );
                    }}
                  >
                    Copiar link
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setShare.mutate({ enabled: false })}
                  >
                    Desligar
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-ghost btn-block"
                onClick={() => setShare.mutate({ enabled: true })}
                disabled={setShare.isPending}
              >
                <IconLink />
                Gerar link público
              </button>
            )}
          </div>

          <button type="button" className="btn btn-ghost btn-block" onClick={exportCsv}>
            <IconDownload />
            Exportar CSV
          </button>

          <button
            type="button"
            className="btn btn-danger btn-block"
            onClick={() => {
              setShareOpen(false);
              setConfirmDelete(true);
            }}
          >
            <IconTrash />
            Apagar rolê
          </button>
        </div>
      </Sheet>

      <ConfirmDialog
        open={confirmDelete}
        title="Apagar o rolê?"
        message="Todas as despesas, divisões e acertos deste rolê vão junto. Não dá para desfazer."
        confirmLabel="Apagar"
        destructive
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          deleteGroup.mutate(groupId, {
            onSuccess: () => {
              toast.success("Rolê apagado.");
              navigate("/app", { replace: true });
            },
            onError: () => toast.error("Não consegui apagar."),
          });
        }}
      />
    </>
  );
}
